/**
 * @module Pages/AdminPages/AdminComponents/TotalGstPaidCard
 * @description This module contains the `TotalGstPaidCard` component, which fetches and displays 
 * the total GST paid. It shows the amount in a dashboard card, and provides navigation to a detailed 
 * report page when clicked.
 */
import { Skeleton } from '@mui/material';
import DashboardCard from 'components/commonComponents/cards/DashboardCard';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTotalGstPaid } from 'services/adminService';
import { fCurrencyRupee } from 'utils/formatNumber';
/**
 * The `TotalGstPaidCard` component fetches the total GST paid data from the backend and 
 * displays it in a dashboard card. The card is clickable, and when clicked, it navigates to a detailed 
 * GST report page.
 * 
 * @component
 * @returns {JSX.Element} A dashboard card displaying the total GST paid.
 */
const TotalGstPaidCard = () => {
  const [totalGstPaidData, setTotalGstPaidData] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  /**
   * Fetches the total GST paid data from the backend and updates the state.
   * 
   * This function is executed inside the `useEffect` hook to fetch data once when the component mounts.
   * 
   * @async
   * @function
   * @returns {void}
   */
  useEffect(() => {
    const fetchGstPaidData = async () => {
      try {
        const response = await fetchTotalGstPaid();
        const { totalGSTAmount } = response.data;
        setTotalGstPaidData({ totalGSTAmount });
      } catch (error) {
        console.error('Error fetching total gst paid data:', error);
      } finally {
        setLoading(false); // Set loading to false after API call
      }
    };

    fetchGstPaidData();
  }, []);
  /**
   * Handles the click event on the dashboard card. Redirects the user to the GST reports page.
   * 
   * @function
   * @returns {void}
   */
  const handleClick = () => {
    navigate('/admin/gstReports', { state: { fromAdminDashboard: true } });
  };

  return (
    <>
      {
        loading ? (
          <Skeleton variant="rectangular" height={135} sx={{ borderRadius: '1.25rem' }} />
        ) : (
          <DashboardCard
            title="Total GST Paid"
            subtitle={fCurrencyRupee(totalGstPaidData.totalGSTAmount || 0)}
            handleClick={handleClick}
            isClickable
          />
        )
      }
    </>
  );
};

export default TotalGstPaidCard;
